import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Star,
  Shield,
  Banknote,
  X,
  Send,
  AlertTriangle,
  Phone,
  Copy,
  Timer,
  ChevronLeft,
  Loader2,
  Flag,
  Paperclip,
  FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogHeader,
} from "@/components/ui/dialog";
import type { ViewMode } from "@/interfaces/p2p";
import { copyToClipboard, normalizeCurrency } from "@/utils";
import { useAppSelector } from "@/store/hooks";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router-dom";
import { useTrade } from "@/hooks";
import Loader from "@/components/loader/Loader";
import { ErrorState } from "@/components/ErrorState";
import { TradeStatus, TradeType, type Trade } from "@/interfaces/trade";
import { formatCurrency } from "@/utils/formatCurrency";
import {
  TradeMessageType,
  type TradeMessage,
} from "@/interfaces/message/message";
import { tradeApi } from "@/services/tradeApi";
import { tradeSocket } from "@/services/tradeSocket";
import { queryClient } from "@/services/queryClient";
import { TRADE_EVENTS } from "@/events/trade.events";
import { getErrorMessage } from "@/utils/getErrorMessage";
import { uploadFiles } from "@/upload/upload";
import { formatTimeLeft } from "@/utils/formatTime";
import { UserVerificationDialog } from "@/components/user-component/UserVerificationDialog";
import TradeMessageItem from "@/components/p2p/TradeMessageItem";

export const TradeView = () => {
  const { theme } = useAppSelector((state) => state.ui);

  const [viewMode, setViewMode] = useState<ViewMode>("market");
  const [messageInput, setMessageInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [openVerificationDialog, setOpenVerificationDialog] = useState(false);
  const [disputeReason, setDisputeReason] = useState("");
  const [disputeEvidence, setDisputeEvidence] = useState<File[]>([]);
  const [disputePreview, setDisputePreview] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const disputeFileInputRef = useRef<HTMLInputElement>(null);
  const [typingUsers, setTypingUsers] = useState<string[]>([]);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);

  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);

  const [timeLeft, setTimeLeft] = useState<string>("");
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const { user } = useAppSelector((state) => state.auth);
  const { id: tradeId } = useParams();
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const userId = user?.id || "";

  const navigate = useNavigate();

  const {
    data: trade,
    isLoading,
    isError,
    refetch,
  } = useTrade(tradeId as string, userId);

  const dispute = trade?.dispute;

  const isBuyer = trade?.buyer?.id === userId;
  const isSeller = trade?.seller?.id === userId;
  const canRelease = isSeller && trade.status === TradeStatus.PAID;

  const canCancel =
    (isBuyer && trade.status === TradeStatus.PENDING) ||
    (isSeller && trade.status === TradeStatus.PENDING);

  const canMarkPaid = isBuyer && trade?.status === TradeStatus.PENDING;

  const canDispute = useMemo(() => {
    const status = trade?.status;

    if (!status) return false;

    const isParticipant = isBuyer || isSeller;
    const isDisputableState = status === TradeStatus.PAID;

    const isFinalState =
      status === TradeStatus.RELEASED ||
      status === TradeStatus.AUTO_RELEASED ||
      status === TradeStatus.CANCELLED ||
      status === TradeStatus.EXPIRED ||
      status === TradeStatus.REFUNDED;
    // status === TradeStatus.DISPUTED;

    return isParticipant && isDisputableState && !isFinalState;
  }, [trade?.status, isBuyer, isSeller]);

  const closedStatuses: TradeStatus[] = [
    TradeStatus.RELEASED,
    TradeStatus.AUTO_RELEASED,
    TradeStatus.CANCELLED,
    TradeStatus.EXPIRED,
    // TradeStatus.DISPUTED,
    TradeStatus.REFUNDED,
  ];

  const canChat = !closedStatuses.includes(trade?.status as TradeStatus);

  const showCountdown =
    canChat && trade?.status !== TradeStatus.EXPIRED && !!trade?.expiresAt;

  const [selectedImagePreview, setSelectedImagePreview] = useState<{
    url: string;
    index: number;
  } | null>(null);

  useEffect(() => {
    if (!trade || isLoading) return;

    const isParticipant =
      trade?.buyer?.id === userId || trade?.seller?.id === userId;

    if (!isParticipant) {
      navigate(-1);
    }
  }, [trade, userId, isLoading, navigate]);

  // Socket setup
  useEffect(() => {
    if (!tradeId || !user?.id) return;

    let mounted = true;

    const socket = tradeSocket.connect();

    const handleNewMessage = (msg: TradeMessage) => {
      if (!mounted) return;

      queryClient.setQueryData(
        ["trade", tradeId, userId],
        (old: Trade | undefined) => {
          if (!old) return old;

          const exists = old.messages?.some((m) => m.id === msg.id);

          if (exists) {
            return old;
          }

          const messages = [...(old.messages || []), msg];

          messages.sort(
            (a, b) =>
              new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
          );

          return {
            ...old,
            messages,
          };
        },
      );
    };

    const handleTradeStatusUpdate = (updatedTrade: Trade) => {
      if (!mounted) return;

      queryClient.setQueryData(
        ["trade", tradeId, userId],
        (old: Trade | undefined) => {
          if (!old) return old;

          return {
            ...old,
            status: updatedTrade.status,
            chatClosed: updatedTrade.chatClosed,
            updatedAt: updatedTrade.updatedAt,
          };
        },
      );
    };

    const handleTyping = ({
      userId: typingUserId,
      isTyping,
    }: {
      userId: string;
      isTyping: boolean;
    }) => {
      if (!mounted) return;

      if (typingUserId === userId) {
        return;
      }

      if (isTyping) {
        setTypingUsers((prev) =>
          prev.includes(typingUserId) ? prev : [...prev, typingUserId],
        );
      } else {
        setTypingUsers((prev) => prev.filter((id) => id !== typingUserId));
      }
    };

    const handleSocketConnect = () => {
      console.log("Trade socket restored");

      void refetch();
    };

    const updateConnectionState = () => {
      setSocketConnected(tradeSocket.isConnected());
    };

    socket.on("connect", updateConnectionState);
    socket.on("disconnect", updateConnectionState);
    socket.on("connect", handleSocketConnect);

    updateConnectionState();

    socket.on(TRADE_EVENTS.SERVER.NEW_MESSAGE, handleNewMessage);

    socket.on(TRADE_EVENTS.SERVER.USER_TYPING, handleTyping);

    socket.on(TRADE_EVENTS.SERVER.PAID, handleTradeStatusUpdate);

    socket.on(TRADE_EVENTS.SERVER.RELEASED, handleTradeStatusUpdate);

    socket.on(TRADE_EVENTS.SERVER.CANCELLED, handleTradeStatusUpdate);

    socket.on(TRADE_EVENTS.SERVER.DISPUTED, handleTradeStatusUpdate);

    socket.on(TRADE_EVENTS.SERVER.EXPIRED, handleTradeStatusUpdate);

    void tradeSocket.joinTrade(tradeId);

    return () => {
      mounted = false;

      socket.off(TRADE_EVENTS.SERVER.NEW_MESSAGE, handleNewMessage);

      socket.off(TRADE_EVENTS.SERVER.USER_TYPING, handleTyping);

      socket.off(TRADE_EVENTS.SERVER.PAID, handleTradeStatusUpdate);

      socket.off(TRADE_EVENTS.SERVER.RELEASED, handleTradeStatusUpdate);

      socket.off(TRADE_EVENTS.SERVER.CANCELLED, handleTradeStatusUpdate);

      socket.off(TRADE_EVENTS.SERVER.DISPUTED, handleTradeStatusUpdate);

      socket.off(TRADE_EVENTS.SERVER.EXPIRED, handleTradeStatusUpdate);

      socket.off("connect", updateConnectionState);
      socket.off("disconnect", updateConnectionState);
      socket.off("connect", handleSocketConnect);

      void tradeSocket.leaveTrade(tradeId);
    };
  }, [tradeId, user?.id]);

  useEffect(() => {
    if (!trade?.expiresAt || !showCountdown) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      setTimeLeft("");

      return;
    }

    const expiresAt = new Date(trade.expiresAt).getTime();

    const updateCountdown = () => {
      const diff = expiresAt - Date.now();

      setTimeLeft(formatTimeLeft(diff));

      if (diff <= 0 && intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };

    updateCountdown();

    intervalRef.current = setInterval(updateCountdown, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [trade?.expiresAt, showCountdown]);

  const tradeBanner = useMemo(() => {
    switch (trade?.status) {
      case TradeStatus.EXPIRED:
        return {
          title: "Trade Expired",
          color: "yellow",
          message:
            "The buyer did not mark payment before the payment deadline. Escrow has been returned to the seller.",
        };

      case TradeStatus.DISPUTED:
        if (trade.dispute) {
          return {
            title: "Release Timeout",
            color: "red",
            message:
              "The seller did not release the crypto before the deadline. This trade has been automatically escalated for review.",
          };
        }

        return null;

      default:
        return null;
    }
  }, [trade]);

  // Auto scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [trade?.messages]);

  const sendMessage = useCallback(async () => {
    if (isSendingMessage) return;

    if (!messageInput.trim() && !selectedImage) {
      return;
    }

    if (!tradeId || !user?.id) return;

    const text = messageInput.trim();

    setIsSendingMessage(true);
    setMessageInput("");

    try {
      let imageUrl: string | undefined;

      if (selectedImage) {
        const [result] = await uploadFiles({
          files: selectedImage,
          folder: "trade-messages",
        });

        imageUrl = result.url;
        setSelectedImage(null);

        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }

      await tradeSocket.sendMessage({
        tradeId,
        message: text || undefined,
        imageUrl,
      });
    } catch (error) {
      console.error(error);

      /**
       * Put the text back into the input if delivery failed.
       */
      setMessageInput(text);

      toast.error(
        getErrorMessage(error) ||
          "Message could not be sent. Please try again.",
      );
    } finally {
      setIsSendingMessage(false);
    }
  }, [isSendingMessage, messageInput, selectedImage, tradeId, user?.id]);

  const handleTyping = () => {
    if (!tradeId) return;
    tradeSocket.startTyping(tradeId);

    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      tradeSocket.stopTyping(tradeId);
    }, 1500);
  };

  const handleFileAttach = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.warning("Only images are allowed");
      return;
    }

    setSelectedImage(file);
  };

  const removeImage = () => {
    setSelectedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const markAsPaid = async () => {
    if (!tradeId) return;
    setIsProcessing(true);
    try {
      await tradeApi.markAsPaid(tradeId);
      toast.success("Payment marked successfully");
      // refetch();

      // tradeSocket.connect();
      // tradeSocket.joinTrade(tradeId);
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to mark as paid");
    } finally {
      setIsProcessing(false);
    }
  };

  const releaseTrade = async ({
    otp,
    securityCode,
  }: {
    otp: string;
    securityCode: string;
  }) => {
    if (!tradeId) return;

    setIsProcessing(true);

    try {
      await tradeApi.releaseTrade(tradeId, {
        otp,
        securityCode,
      });

      queryClient.invalidateQueries({
        queryKey: ["advertisments:all"],
      });

      await queryClient.invalidateQueries({
        queryKey: ["profile", userId],
      });

      // await queryClient.invalidateQueries({
      //   queryKey: ["trade", tradeId],
      // });

      // await queryClient.invalidateQueries({
      //   queryKey: ["trades"],
      // });

      await queryClient.invalidateQueries({
        queryKey: ["wallet", userId],
      });

      toast.success("Crypto released successfully");
      // refetch();
      setOpenVerificationDialog(false);
    } catch (error) {
      toast.error(getErrorMessage(error));
      console.log(error);
    } finally {
      setIsProcessing(false);
    }
  };

  const cancelTrade = async () => {
    if (!tradeId) return;
    setIsProcessing(true);
    try {
      await tradeApi.cancelTrade(tradeId);

      queryClient.invalidateQueries({
        queryKey: ["advertisments:all"],
      });
      toast.success("Trade cancelled");
      setShowCancelConfirm(false);
      // refetch();
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to cancel");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDisputeFileAttach = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const imageFiles = files.filter((file) => file.type.startsWith("image/"));

    if (imageFiles.length !== files.length) {
      toast.warning("Only image files are allowed");
    }

    const totalAfterAdd = disputeEvidence.length + imageFiles.length;

    if (totalAfterAdd > 4) {
      toast.warning("Maximum of 4 images allowed");
      return;
    }

    const newPreviews = imageFiles.map((file) => URL.createObjectURL(file));

    setDisputeEvidence((prev) => [...prev, ...imageFiles]);
    setDisputePreview((prev) => [...prev, ...newPreviews]);

    toast.success(`${imageFiles.length} file(s) attached`);
  };

  const submitDispute = async () => {
    if (!disputeReason.trim()) {
      toast.warning("Please describe the issue.");
      return;
    }

    if (disputeEvidence.length >= 4) {
      toast.warning("You already selected 4 images");
      return;
    }

    setIsProcessing(true);

    try {
      let evidenceUrls: string[] = [];

      if (disputeEvidence.length > 0) {
        const results = await uploadFiles({
          files: disputeEvidence,
          folder: "trade-disputes",
        });

        evidenceUrls = results.map((r: any) => r.url);
      }

      const payload = {
        reason: disputeReason,
        images: evidenceUrls,
      };

      await tradeApi.openDispute(trade?.id!, payload);

      toast.success("Dispute submitted successfully");

      setDisputeReason("");
      setDisputeEvidence([]);
      setDisputePreview([]);
      setShowDisputeModal(false);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsProcessing(false);
    }
  };

  const getStatusBadge = (status: TradeStatus) => {
    const styles: Record<TradeStatus, string> = {
      PENDING: "bg-yellow-500/20 text-yellow-500",
      PAID: "bg-blue-500/20 text-blue-500",
      RELEASED: "bg-green-500/20 text-green-500",
      AUTO_RELEASED: "bg-emerald-500/20 text-emerald-500",
      DISPUTED: "bg-red-500/20 text-red-500",
      UNDER_REVIEW: "bg-orange-500/20 text-orange-500",
      CANCELLED: "bg-gray-500/20 text-gray-500",
      EXPIRED: "bg-slate-500/20 text-slate-500",
      REFUNDED: "bg-purple-500/20 text-purple-500",
    };

    return (
      <span
        className={`px-3 py-1 rounded-full text-xs font-medium ${styles[status]}`}
      >
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (isLoading) return <Loader type="bounce" />;

  if (isError)
    return (
      <ErrorState
        onRetry={() => {
          void refetch();
        }}
      />
    );

  return (
    <div className="max-w-4xl mx-auto mt-20">
      {/* Trade Header */}
      <div
        className={`rounded-xl border p-6 mb-6 ${theme === "dark" ? "bg-card border-border" : "bg-white border-gray-200"}`}
      >
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setViewMode("market")}
            className={`flex items-center gap-2 text-sm ${theme === "dark" ? "text-muted-foreground hover:text-foreground" : "text-gray-500 hover:text-gray-900"}`}
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Market
          </button>
          {getStatusBadge(trade?.status as TradeStatus)}
        </div>

        <div className="flex items-center justify-between">
          <div>
            <h2
              className={`text-xl font-bold mb-1 ${theme === "dark" ? "text-foreground" : "text-gray-900"}`}
            >
              {isBuyer ? "Buying" : "Selling"}{" "}
              {trade?.price && Number(trade.price) !== 0
                ? (Number(trade.amount) ?? 0).toFixed(2)
                : 0}{" "}
              {normalizeCurrency(trade?.advertisement?.asset?.currency ?? "")}
            </h2>
            <p
              className={`text-sm ${theme === "dark" ? "text-muted-foreground" : "text-gray-500"}`}
            >
              Trade ID: {trade?.id}
            </p>
          </div>
          <div className="text-right">
            <p
              className={`text-2xl font-bold font-mono ${theme === "dark" ? "text-foreground" : "text-gray-900"}`}
            >
              {formatCurrency(Number(trade?.fiatAmount))}{" "}
              {normalizeCurrency(
                trade?.advertisement?.fiatCurrency?.code ?? "",
              )}
            </p>
            <p
              className={`text-sm ${theme === "dark" ? "text-muted-foreground" : "text-gray-500"}`}
            >
              @ {trade?.advertisement?.price}{" "}
              {trade?.advertisement?.fiatCurrency &&
                normalizeCurrency(trade?.advertisement?.fiatCurrency?.code)}
              /
              {trade?.advertisement?.asset &&
                normalizeCurrency(trade?.advertisement?.asset?.currency ?? "")}
            </p>
          </div>
        </div>

        {showCountdown && (
          <div
            className={`mt-4 p-3 rounded-lg flex items-center gap-3 ${theme === "dark" ? "bg-yellow-500/10" : "bg-yellow-50"}`}
          >
            <Timer className="w-5 h-5 text-yellow-500" />
            <span className="text-yellow-500 font-medium">
              Time remaining: {timeLeft}
            </span>
          </div>
        )}

        {tradeBanner && (
          <div
            className={`mt-4 rounded-lg border p-4 ${
              tradeBanner.color === "red"
                ? "border-red-500 bg-red-500/10"
                : "border-yellow-500 bg-yellow-500/10"
            }`}
          >
            <h4 className="font-semibold">{tradeBanner.title}</h4>
            <p className="text-sm mt-1">{tradeBanner.message}</p>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left Panel - Trade Details */}
        <div className="space-y-6">
          {/* Counterparty Info */}
          <div
            className={`rounded-xl border p-5 ${theme === "dark" ? "bg-card border-border" : "bg-white border-gray-200"}`}
          >
            <h3
              className={`font-semibold mb-4 ${theme === "dark" ? "text-foreground" : "text-gray-900"}`}
            >
              {isBuyer ? "Seller" : "Buyer"} Information
            </h3>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#F0B90B] to-[#E07B39] flex items-center justify-center text-black font-bold text-lg">
                {isBuyer ? (trade?.seller?.avatar ?? "") : trade?.buyer?.avatar}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span
                    className={`font-semibold ${theme === "dark" ? "text-foreground" : "text-gray-900"}`}
                  >
                    {isBuyer
                      ? trade?.seller?.merchantName ||
                        trade?.seller?.firstName + " " + trade?.seller?.lastName
                      : trade?.buyer?.merchantName ||
                        trade?.buyer?.firstName + " " + trade?.buyer?.lastName}
                  </span>
                  <Shield className="w-4 h-4 text-primary" />
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <span className="flex items-center gap-1 text-primary">
                    <Star className="w-3 h-3 fill-[#F0B90B]" />
                    {isBuyer
                      ? trade?.seller?.reputationScore
                      : trade?.buyer?.reputationScore}
                  </span>
                  <span
                    className={
                      theme === "dark"
                        ? "text-muted-foreground"
                        : "text-gray-500"
                    }
                  >
                    {isBuyer
                      ? trade?.seller?.buyerTrades
                      : trade?.buyer?.sellerTrades}{" "}
                    trades
                  </span>
                  <span
                    className={
                      theme === "dark"
                        ? "text-muted-foreground"
                        : "text-gray-500"
                    }
                  >
                    {isBuyer
                      ? trade?.seller?.completedTrades
                      : trade?.buyer?.completedTrades}
                    %
                  </span>
                </div>
              </div>
            </div>

            {isBuyer
              ? trade?.seller?.phoneNumber && (
                  <div
                    className={`flex items-center gap-2 p-3 rounded-lg ${theme === "dark" ? "bg-muted" : "bg-gray-100"}`}
                  >
                    <Phone className="w-4 h-4 text-primary" />
                    <span
                      className={
                        theme === "dark" ? "text-foreground" : "text-gray-900"
                      }
                    >
                      {trade?.seller?.phoneNumber}
                    </span>
                    <button
                      onClick={() =>
                        copyToClipboard(trade?.seller?.phoneNumber!)
                      }
                      className="ml-auto text-muted-foreground hover:text-primary"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                )
              : trade?.buyer?.phoneNumber && (
                  <div
                    className={`flex items-center gap-2 p-3 rounded-lg ${theme === "dark" ? "bg-muted" : "bg-gray-100"}`}
                  >
                    <Phone className="w-4 h-4 text-primary" />
                    <span
                      className={
                        theme === "dark" ? "text-foreground" : "text-gray-900"
                      }
                    >
                      {trade?.buyer?.phoneNumber}
                    </span>
                    <button
                      onClick={() =>
                        copyToClipboard(trade?.buyer?.phoneNumber!)
                      }
                      className="ml-auto text-muted-foreground hover:text-primary"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                )}
          </div>

          {/* Payment Details */}
          <div
            className={`rounded-xl border p-5 ${theme === "dark" ? "bg-card border-border" : "bg-white border-gray-200"}`}
          >
            <h3
              className={`font-semibold mb-4 ${theme === "dark" ? "text-foreground" : "text-gray-900"}`}
            >
              Payment Details
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span
                  className={
                    theme === "dark" ? "text-muted-foreground" : "text-gray-500"
                  }
                >
                  Method
                </span>
                <span
                  className={
                    theme === "dark" ? "text-foreground" : "text-gray-900"
                  }
                >
                  {trade?.paymentMethod?.type || "N/A"}
                </span>
              </div>
              <div className="flex justify-between">
                <span
                  className={
                    theme === "dark" ? "text-muted-foreground" : "text-gray-500"
                  }
                >
                  Amount
                </span>
                <span
                  className={`font-mono ${theme === "dark" ? "text-foreground" : "text-gray-900"}`}
                >
                  {formatCurrency(Number(trade?.fiatAmount).toFixed(2))}{" "}
                  {trade?.advertisement?.fiatCurrency &&
                    normalizeCurrency(trade?.advertisement?.fiatCurrency?.code)}
                </span>
              </div>
              <div className="flex justify-between">
                <span
                  className={
                    theme === "dark" ? "text-muted-foreground" : "text-gray-500"
                  }
                >
                  Reference
                </span>
                <div className="flex items-center gap-2">
                  <span
                    className={`font-mono ${theme === "dark" ? "text-foreground" : "text-gray-900"}`}
                  >
                    {trade?.id.slice(0, 15)}...
                  </span>
                  <button
                    onClick={() => copyToClipboard(trade?.id as string)}
                    className="text-muted-foreground hover:text-primary"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Bank Details (for buyers) */}
          {trade?.advertisement?.type === TradeType.BUY &&
            trade?.paymentSnapshot &&
            trade?.paymentMethod && (
              <div
                className={`rounded-xl border p-5 ${theme === "dark" ? "bg-card border-border" : "bg-white border-gray-200"}`}
              >
                <h3
                  className={`font-semibold mb-4 flex items-center gap-2 ${theme === "dark" ? "text-foreground" : "text-gray-900"}`}
                >
                  <Banknote className="w-5 h-5 text-primary" />
                  Seller's Bank Details
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span
                      className={
                        theme === "dark"
                          ? "text-muted-foreground"
                          : "text-gray-500"
                      }
                    >
                      Bank Name
                    </span>
                    <span
                      className={
                        theme === "dark" ? "text-foreground" : "text-gray-900"
                      }
                    >
                      {trade?.paymentMethod?.bank?.name}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span
                      className={
                        theme === "dark"
                          ? "text-muted-foreground"
                          : "text-gray-500"
                      }
                    >
                      Account Name
                    </span>
                    <span
                      className={
                        theme === "dark" ? "text-foreground" : "text-gray-900"
                      }
                    >
                      {trade?.paymentMethod?.accountName}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span
                      className={
                        theme === "dark"
                          ? "text-muted-foreground"
                          : "text-gray-500"
                      }
                    >
                      Account Number
                    </span>
                    <div className="flex items-center gap-2">
                      <span
                        className={`font-mono ${theme === "dark" ? "text-foreground" : "text-gray-900"}`}
                      >
                        {trade?.paymentMethod?.accountNumber}
                      </span>
                      <button
                        onClick={() => {
                          const accountNumber =
                            trade?.paymentMethod?.accountNumber;

                          if (!accountNumber) return;

                          copyToClipboard(accountNumber);
                        }}
                        className="text-muted-foreground hover:text-primary"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

          {/* Terms & Conditions */}
          {trade?.advertisement?.terms && (
            <div
              className={`rounded-xl border p-5 ${theme === "dark" ? "bg-card border-border" : "bg-white border-gray-200"}`}
            >
              <h3
                className={`font-semibold mb-4 flex items-center gap-2 ${theme === "dark" ? "text-foreground" : "text-gray-900"}`}
              >
                <FileText className="w-5 h-5 text-primary" />
                Terms & Conditions
              </h3>
              <ul
                className={`text-sm ${theme === "dark" ? "text-muted-foreground" : "text-gray-600"}`}
              >
                {trade?.advertisement?.terms.map((term) => (
                  <li key={term.id}>{term.description}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            {canMarkPaid && (
              <button
                onClick={markAsPaid}
                disabled={isProcessing}
                className="w-full py-3 rounded-lg bg-[#0ECB81] text-white font-semibold hover:brightness-110 transition-all disabled:opacity-50"
              >
                {isProcessing ? (
                  <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                ) : (
                  "I Have Paid"
                )}
              </button>
            )}
          </div>

          <div className="space-y-3">
            {canRelease && (
              <button
                onClick={() => setOpenVerificationDialog(true)}
                disabled={isProcessing}
                className="w-full py-3 rounded-lg bg-[#F0B90B] text-black font-semibold"
              >
                Release Crypto
              </button>
            )}
          </div>

          <div className="flex gap-3">
            {canCancel && (
              <button
                onClick={() => setShowCancelConfirm(true)}
                className={`flex-1 py-3 rounded-lg font-medium transition-colors ${
                  theme === "dark"
                    ? "bg-muted text-foreground hover:bg-[#343A42]"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Cancel
              </button>
            )}
          </div>

          <div className="flex gap-3">
            {canDispute && (
              <div className="flex flex-col gap-1 w-full">
                <button
                  onClick={() => setShowDisputeModal(true)}
                  className="w-full p-4 rounded-xl border border-red-500/20 bg-gradient-to-r from-red-500/5 to-transparent hover:from-red-500/10 hover:border-red-500/40  transition-all duration-200 text-left flex items-center justify-between "
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-red-500/10">
                      <Flag className="w-4 h-4 text-red-500" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-red-500">
                        Open Dispute
                      </p>
                      <p className="text-xs text-red-400/70">
                        Report an issue with this trade
                      </p>
                    </div>
                  </div>

                  <span className="text-red-500/60 text-sm">›</span>
                </button>
              </div>
            )}
          </div>

          {trade?.status === TradeStatus.DISPUTED && (
            <div className="p-3 text-sm text-red-500 bg-red-500/10 rounded-lg">
              Trade under dispute review
            </div>
          )}

          {/* {trade?.status === TradeStatus.PAID && (
            <div className="p-4 rounded-lg bg-green-500/10 text-center">
              <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto mb-2" />
              <p className="text-green-500 font-medium">
                Trade completed successfully!
              </p>
            </div>
          )} */}
        </div>

        {/* Right Panel - Chat */}
        <div
          className={`rounded-xl border flex flex-col h-[600px] ${theme === "dark" ? "bg-card border-border" : "bg-white border-gray-200"}`}
        >
          <div className="flex items-center justify-between p-4">
            <h3
              className={`font-semibold ${
                theme === "dark" ? "text-foreground" : "text-gray-900"
              }`}
            >
              Trade Chat
            </h3>

            <div className="flex items-center gap-2 text-xs">
              <span
                className={`w-2 h-2 rounded-full ${
                  socketConnected
                    ? "bg-emerald-500"
                    : "bg-yellow-500 animate-pulse"
                }`}
              />

              <span className="text-muted-foreground">
                {socketConnected ? "Connected" : "Reconnecting..."}
              </span>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {trade?.messages?.map((msg) => (
              <TradeMessageItem
                key={msg.id}
                msg={msg}
                dispute={dispute}
                trade={trade}
              />
            ))}
            <div ref={messagesEndRef} />
          </div>

          {canChat && (
            <div
              className={`p-4 border-t ${theme === "dark" ? "border-border" : "border-gray-200"}`}
            >
              {selectedImage && (
                <div className="relative w-24 h-24 mb-2">
                  <img
                    src={URL.createObjectURL(selectedImage)}
                    className="w-24 h-24 object-cover rounded-lg border"
                  />
                  <button
                    onClick={removeImage}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs"
                  >
                    ×
                  </button>
                </div>
              )}
              {typingUsers.length > 0 && (
                <div className="px-4 pb-2 text-xs text-muted-foreground">
                  Someone is typing...
                </div>
              )}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      void sendMessage();
                      return;
                    }

                    handleTyping();
                  }}
                  placeholder="Type a message..."
                  className={`flex-1 px-4 py-2 rounded-lg border text-sm focus:outline-none focus:border-primary ${
                    theme === "dark"
                      ? "bg-muted border-border text-foreground placeholder:text-muted-foreground"
                      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400"
                  }`}
                />
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  onChange={handleFileAttach}
                  accept="image/*,.pdf,.doc,.docx"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className={`p-2 rounded-lg transition-colors ${
                    theme === "dark"
                      ? "bg-muted text-muted-foreground hover:text-foreground"
                      : "bg-gray-100 text-gray-500 hover:text-gray-900"
                  }`}
                >
                  <Paperclip className="w-5 h-5" />
                </button>
                <button
                  onClick={sendMessage}
                  disabled={
                    isSendingMessage || (!messageInput.trim() && !selectedImage)
                  }
                  className="p-2 rounded-lg bg-[#F0B90B] text-black hover:brightness-110 transition-all disabled:opacity-50"
                >
                  {isSendingMessage ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          )}
          {/* <div ref={messagesEndRef} /> */}
        </div>
      </div>

      {/* Cancel Confirmation Modal */}
      <Dialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-yellow-500" />
              Cancel Trade?
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel this trade? This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setShowCancelConfirm(false)}
            >
              Keep Trading
            </Button>
            <Button
              variant="destructive"
              className="flex-1"
              onClick={cancelTrade}
              disabled={isProcessing}
            >
              {isProcessing ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <X className="w-4 h-4 mr-2" />
              )}
              Cancel Trade
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dispute Modal */}
      <Dialog open={showDisputeModal} onOpenChange={setShowDisputeModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-500">
              <Flag className="w-6 h-6" />
              Open Dispute
            </DialogTitle>
            <DialogDescription>
              Please describe the issue. Our support team will review and
              assist.
            </DialogDescription>
          </DialogHeader>
          {trade?.status === TradeStatus.DISPUTED && (
            <div className="mb-3 rounded-lg border border-orange-500/30 bg-orange-500/10 p-3">
              <p className="text-sm font-medium text-orange-500">
                This trade is currently under dispute.
              </p>
              <p className="text-xs text-orange-400 mt-1">
                You can continue communicating with the counterparty and the
                dispute moderator. All messages are recorded as part of the
                dispute review.
              </p>
            </div>
          )}
          <div className="space-y-4 pt-4">
            <textarea
              value={disputeReason}
              onChange={(e) => setDisputeReason(e.target.value)}
              placeholder="Describe the issue..."
              rows={4}
              className={`w-full px-4 py-3 rounded-lg border text-sm focus:outline-none focus:border-primary ${
                theme === "dark"
                  ? "bg-muted border-border text-foreground placeholder:text-muted-foreground"
                  : "bg-white border-gray-200 text-gray-900 placeholder-gray-400"
              }`}
            />
            <div>
              <input
                type="file"
                ref={disputeFileInputRef}
                className="hidden"
                onChange={handleDisputeFileAttach}
                accept="image/*"
                multiple
              />
              <Button
                variant="outline"
                className="w-full"
                onClick={() => disputeFileInputRef.current?.click()}
              >
                <Paperclip className="w-4 h-4 mr-2" />
                {disputeEvidence.length > 0
                  ? `Evidence (${disputeEvidence.length}/4)`
                  : "Attach Evidence"}{" "}
                (4 Max)
              </Button>
              {disputePreview.length > 0 && (
                <div className="grid grid-cols-2 gap-2 mt-3">
                  {disputePreview.map((src, idx) => (
                    <div key={idx} className="relative">
                      <img
                        src={src}
                        className="w-full h-32 object-cover rounded-lg border"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setDisputeEvidence((prev) =>
                            prev.filter((_, i) => i !== idx),
                          );
                          setDisputePreview((prev) =>
                            prev.filter((_, i) => i !== idx),
                          );
                        }}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowDisputeModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={submitDispute}
                disabled={!disputeReason.trim() || isProcessing}
              >
                {isProcessing ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Flag className="w-4 h-4 mr-2" />
                )}
                Submit Dispute
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <UserVerificationDialog
        open={openVerificationDialog}
        onOpenChange={setOpenVerificationDialog}
        loading={isProcessing}
        onVerify={releaseTrade}
        isP2PRelease
      />

      {selectedImagePreview && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center"
          onClick={() => setSelectedImagePreview(null)}
        >
          <div
            className="max-w-4xl w-full p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedImagePreview.url}
              className="w-full max-h-[80vh] object-contain rounded-lg border"
            />

            <div className="flex justify-between mt-3">
              <button
                onClick={() => setSelectedImagePreview(null)}
                className="text-white text-sm"
              >
                Close
              </button>

              {/* OPTIONAL ADMIN CHECK PLACEHOLDER */}
              {user?.role === "ADMIN" && (
                <span className="text-xs text-green-400">
                  Admin View Enabled
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
